import { useState, useEffect, useRef, useCallback } from 'react';
import type { SearchSection, SearchDisplayItem } from '@/contasis/layout/SearchBar/types/search.types';
import { buscarGlobalService } from '@/services/api/buscarGlobal.service';
import type { BuscarGlobalItemDto } from '@/services/api/buscarGlobal.service';

const DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 2;

type SectionConfig = { title: string; routeBase: string; amountLabel?: string };

const SECTION_CONFIG: Record<string, SectionConfig> = {
  comprobante:        { title: 'Comprobantes',          routeBase: '/comprobantes', amountLabel: 'Total' },
  cliente:            { title: 'Clientes',              routeBase: '/clientes' },
  producto:           { title: 'Productos',             routeBase: '/productos', amountLabel: 'Precio' },
  cotizacion:         { title: 'Cotizaciones',           routeBase: '/documentos-comerciales', amountLabel: 'Total' },
  orden_venta:        { title: 'Órdenes de Venta',       routeBase: '/documentos-comerciales', amountLabel: 'Total' },
  nota_venta:         { title: 'Notas de Venta',         routeBase: '/documentos-comerciales', amountLabel: 'Total' },
  orden_compra:       { title: 'Órdenes de Compra',      routeBase: '/compras', amountLabel: 'Total' },
  requerimiento:      { title: 'Requerimientos',         routeBase: '/compras', amountLabel: 'Total' },
  comprobante_compra: { title: 'Comprobantes de Compra', routeBase: '/compras', amountLabel: 'Total' },
  cobranza:           { title: 'Cobranzas',              routeBase: '/cobranzas', amountLabel: 'Total' },
};

function mapToSections(
  items: BuscarGlobalItemDto[],
): Record<string, SearchSection<BuscarGlobalItemDto>> {
  const sections: Record<string, SearchSection<BuscarGlobalItemDto>> = {};

  for (const item of items) {
    const config = SECTION_CONFIG[item.tipo];
    if (!config) continue;

    if (!sections[item.tipo]) {
      sections[item.tipo] = {
        title: config.title,
        routeBase: config.routeBase,
        items: [],
        total: 0,
        hasMore: false,
      };
    }

    const display: SearchDisplayItem<BuscarGlobalItemDto> = {
      id: item.id,
      type: item.tipo,
      entity: item,
      title: item.titulo,
      subtitle: item.subtitulo ?? undefined,
      meta: item.meta ?? undefined,
      amountValue: item.monto ?? undefined,
      amountCurrency: item.moneda ?? 'S/',
      amountLabel: config.amountLabel,
      score: 1,
    };

    sections[item.tipo].items.push(display);
    sections[item.tipo].total++;
  }

  return sections;
}

export const useGlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [sections, setSections] = useState<Record<string, SearchSection<BuscarGlobalItemDto>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    setQuery('');
    setSections({});
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSections({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    timerRef.current = setTimeout(async () => {
      try {
        const items = await buscarGlobalService.buscar(trimmed);
        setSections(mapToSections(items));
      } catch {
        setSections({});
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query]);

  const hasResults = Object.values(sections).some(s => s.items.length > 0);
  const shouldSearch = query.trim().length >= MIN_QUERY_LENGTH;

  return { query, setQuery, sections, isLoading, hasResults, shouldSearch, clear };
};
